import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';

/**
 * SD-9732. How the browser saw a Financial Connections session end.
 *
 * These are FACTS the client can state, never verdicts. `empty` is deliberately not "abandoned":
 * today a closed window and a completed manual entry both resolve with zero accounts and no
 * error, and the server settles which it was from the session object (SD-10156). `cancelled` is
 * reserved for Stripe's cancellation-as-error preview, which reports a close as an error code
 * instead of a succeeded session with no accounts; nothing produces it until that is enabled.
 */
export const FcSessionOutcomes = {
    /** At least one account came back. */
    COMPLETED: 'completed',
    /** Succeeded with zero accounts and no error — the ambiguous shape this ticket exists to classify. */
    EMPTY: 'empty',
    /** Reserved: Stripe's cancellation-as-error preview. Not produced until it is on. */
    CANCELLED: 'cancelled',
    /** Stripe returned an error, or the popup promise rejected. */
    ERROR: 'error',
} as const;

export type FcSessionOutcome = (typeof FcSessionOutcomes)[keyof typeof FcSessionOutcomes];

/**
 * SD-10394. Stripe's auth-flow events preview, enabled on the platform account 2026-09-23.
 *
 * Opted into on `loadStripe({ betas })`, NOT on the collect call. Passing `onEvent` without this
 * flag is silently inert — no error, no events, and an integration that looks like it works — so
 * the two are wired in the same place and neither is meaningful alone.
 */
export const FC_ON_EVENT_BETA = 'financial_connections_on_event_beta_1';

/**
 * SD-10394. Mirrors `RecordFinancialConnectionsSessionOutcomeRequest::MAX_EVENTS`.
 *
 * The server enforces this as an array `max`, which REJECTS rather than truncates: a 51st event
 * 422s the whole request, losing `durationMs`, `outcome` and `accountsReturned` along with it — on
 * precisely the sessions that had the most to say. So the client slices before posting.
 */
export const FC_MAX_EVENTS = 50;

/**
 * SD-10394. Mirrors `RecordFinancialConnectionsSessionOutcomeRequest::MAX_EVENT_TOKEN_LENGTH`.
 *
 * Same all-or-nothing failure as the count: one over-long token 422s the entire report, so both
 * Stripe-supplied strings are clamped rather than risking the row.
 */
export const FC_MAX_EVENT_TOKEN_LENGTH = 64;

/**
 * How many real events survive a truncation. The marker and the terminal event take the other two
 * slots, so a truncated list is exactly FC_MAX_EVENTS long.
 */
const FC_EVENTS_HEAD_BUDGET = FC_MAX_EVENTS - 2;

/**
 * SD-9732 / SD-10394. Event names this client records around the popup.
 *
 * Producers: Stripe's auth-flow events preview (SD-10394), the `code` off a returned StripeError,
 * and the two relink verdicts Stripe states on the session (`relink_result`). The server stores
 * whatever name it is given, so Stripe's names are carried as their WIRE VALUES verbatim — renaming
 * them would only make our records and Stripe's vocabulary disagree.
 *
 * Note `ERROR` is deliberately shared: Stripe's preview emits `error` for an in-flow failure and we
 * already derived `error` from a returned StripeError. Same name, same meaning, and a session can
 * legitimately carry both — the in-flow event and the result-level one.
 */
export const FcAuthFlowEvents = {
    ERROR: 'error',
    /** SD-9989 relink: Stripe confirmed the pinned account was re-authorized (`relink_result.account`). */
    RELINK_SUCCEEDED: 'relink_succeeded',
    /** SD-9989 relink: Stripe stated why the relink failed (`relink_result.failure_reason` rides `code`). */
    RELINK_FAILED: 'relink_failed',
    /** SD-10394: Stripe's auth-flow event names, verbatim. */
    OPEN: 'open',
    MANUAL_ENTRY_INITIATED: 'manual_entry_initiated',
    CONSENT_ACQUIRED: 'consent_acquired',
    SEARCH_INITIATED: 'search_initiated',
    INSTITUTION_SELECTED: 'institution_selected',
    INSTITUTION_AUTHORIZED: 'institution_authorized',
    ACCOUNTS_SELECTED: 'accounts_selected',
    SUCCESS: 'success',
    /**
     * SD-10394: the customer closed the modal without completing. This is the signal SD-9590 has
     * been missing — it separates a closed popup from a real attempt that returned nothing, which
     * until now were byte-identical to us.
     */
    CANCEL: 'cancel',
    /**
     * SD-10394: this list was sliced to fit FC_MAX_EVENTS; `code` is null and `atMs` is the offset
     * of the FIRST dropped event. Present only when truncation actually happened.
     */
    EVENTS_TRUNCATED: 'events_truncated',
} as const;

/**
 * One observation during the popup, stamped as a monotonic offset from when it opened.
 */
export interface FcAuthFlowEvent {
    event: string;
    /** Stripe's error/event code when it carries one; null otherwise. */
    code: string | null;
    /** Milliseconds since the popup opened (performance.now() delta, so clock changes cannot skew it). */
    atMs: number;
}

/**
 * SD-10394. Clamp a Stripe-supplied token to what the server will accept.
 *
 * Returns null for anything empty: the server declares `events.*.event` `required`, which rejects
 * an empty string, and one such entry 422s the WHOLE report. A nameless event is dropped rather
 * than allowed to take the session's duration and outcome down with it.
 */
export function clampFcEventToken(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }

    return value.slice(0, FC_MAX_EVENT_TOKEN_LENGTH);
}

/**
 * SD-10394. Enforce the server's per-token rules over EVERY event, whoever produced it.
 *
 * Review finding F-116e: the clamp used to live only in the `onEvent` handler, so the two events
 * derived from Stripe's RESULT — the StripeError code and a relink `failure_reason` — still reached
 * the POST unclamped. Both are Stripe-supplied strings, both ride the same request, and one over-long
 * token 422s the whole report: `durationMs`, `outcome` and `accountsReturned` go with it, on exactly
 * the error and failed-relink sessions that have the most to say. Enforcing it here means this method
 * really does own BOTH server limits, which is what its comment already claimed.
 *
 * A nameless event is dropped rather than sent blank, for the same reason: `events.*.event` is
 * `required` server-side, which rejects an empty string.
 */
export function normalizeFcAuthFlowEvents(events: FcAuthFlowEvent[]): FcAuthFlowEvent[] {
    return events.reduce<FcAuthFlowEvent[]>((kept, event) => {
        const name = clampFcEventToken(event?.event);

        if (name) {
            kept.push({
                event: name,
                code: clampFcEventToken(event?.code),
                atMs: event.atMs,
            });
        }

        return kept;
    }, []);
}

/**
 * SD-10394. Slice an event list to what the server accepts, and SAY SO in the list itself.
 *
 * Keeps the first FC_EVENTS_HEAD_BUDGET events, then the marker, then the terminal event — not the
 * last fifty. Three reasons:
 *
 *  - `atMs` is a self-anchoring offset, so early events are not needed to date later ones.
 *  - The early events are where the institution path is visible (which bank, which route).
 *  - The terminal event is the verdict, and pinning it explicitly guarantees it survives rather
 *    than hoping it lands inside a window.
 *
 * The marker exists because a silently sliced array is indistinguishable from a complete one at the
 * read: someone counting events per session would get a number bounded at fifty and read it as what
 * the session emitted — a count carried without its bound. `events.*.event` is a free string on the
 * server, so the truncation can declare itself without any schema change.
 */
export function truncateFcAuthFlowEvents(events: FcAuthFlowEvent[]): FcAuthFlowEvent[] {
    if (events.length <= FC_MAX_EVENTS) {
        return events;
    }

    return [
        ...events.slice(0, FC_EVENTS_HEAD_BUDGET),
        {
            event: FcAuthFlowEvents.EVENTS_TRUNCATED,
            code: null,
            atMs: events[FC_EVENTS_HEAD_BUDGET].atMs,
        },
        events[events.length - 1],
    ];
}

/**
 * SD-9732. What the browser observed around a Financial Connections popup.
 *
 * `durationMs` was the whole signal when this shipped as a stopwatch: Stripe reported nothing
 * about how a session ended, so a customer who closed the popup and one who completed the flow
 * and received nothing were identical to us. `outcome` and `events` carry what we can state
 * today and are the landing zone for Stripe's auth-flow events preview.
 */
export interface FcSessionOutcomeRequest {
    sessionId: string;
    durationMs: number;
    accountsReturned: number;
    reportedError: boolean;
    outcome: FcSessionOutcome;
    events: FcAuthFlowEvent[];
}

/**
 * SD-9732. Reports how a Financial Connections session ended.
 *
 * Exists because Stripe provides NO cancellation or abandonment signal: its
 * `FinancialConnectionsSessionResult` is a two-arm union with no cancellation variant, and the
 * session object carries no status (verified against @stripe/stripe-js 7.9.0). So SD-9590's 128
 * no-return sessions cannot be split into "closed the popup" and "completed and got nothing" —
 * the split Stripe's own triage asks for — from anything Stripe sends us.
 *
 * What we DO control is the clock around the popup. A session resolving in seconds reads as an
 * abandon; one resolving after minutes with zero accounts was a real attempt that returned nothing.
 *
 * Modelled on TimeoutLoggingService, which is the only surviving UI -> server event emitter in this
 * repo: `ClientErrorLoggingService` and the Better Stack client sink were removed (SD-7666/7717) and
 * UI Sentry is dead config, so there is no generic client sink to reuse.
 */
@Injectable({
    providedIn: 'root',
})
export class FinancialConnectionsTelemetryService {
    constructor(private _http: ApplicationHttpClient) {}

    /**
     * Fire-and-forget. Telemetry must never affect the journey it measures, so every failure is
     * swallowed — the customer's bank link does not fail because a diagnostic POST did.
     *
     * Callers may ignore the returned Observable, but it is COLD: nothing is sent until something
     * subscribes. `reportSessionOutcome(...).subscribe()` is the intended call shape.
     */
    public reportSessionOutcome(
        organizationId: number,
        request: FcSessionOutcomeRequest,
    ): Observable<void> {
        // The session id rides the route (the same shape as the SD-10156 `resolve` and SD-10187
        // `inspect` siblings) and is stripped from the body so it is not stated twice.
        const { sessionId, ...body } = request;
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessionOutcome(organizationId, sessionId),
        );

        // SD-10394: both server limits are enforced here rather than at each producer -- this
        // method owns the contract with the endpoint, so every caller is covered once. Normalise
        // BEFORE truncating, so the events that survive the slice are fifty real ones (F-116e).
        const payload = {
            ...body,
            events: truncateFcAuthFlowEvents(normalizeFcAuthFlowEvents(body.events ?? [])),
        };

        return this._http.post<void>(url, JSON.stringify(payload)).pipe(
            catchError((error) => {
                console.error('Failed to report Financial Connections session outcome:', error);
                return of(void 0);
            }),
        );
    }
}
